/**
 * The completed panel (docs/ux/casefile.md): machine versus reviewed in
 * figures, the sentence that the machine result is kept, the "What changed"
 * table of corrections with the machine and human sentences, the accepted
 * items folded away, the two deliverables, and the gate line.
 */

import { statusLabel, type Status } from '@beam/core/status';
import { ChevronRightIcon } from 'lucide-react';
import type { CasefileManifest, SortReviewItem, SortRunHead } from '@/app/types';
import { StatusChip } from '@/components/matter/StatusChip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { dateTime } from '@/lib/format';
import { describeHuman, describeMachine, pagesText } from './describe';
import { OpenPdfLink } from './ReplayPanel';
import { DecisionMark } from './ReviewQueue';
import { sortedPdfUrl } from './useSort';

function Figure({ label, machine, reviewed }: { label: string; machine: string; reviewed: string }) {
  return (
    <tr className="border-t border-beam-line">
      <th scope="row" className="py-2 pr-4 text-left text-[13px] font-normal text-beam-text-muted">{label}</th>
      <td className="tabular py-2 pr-6 text-right text-[16px] font-medium xl:text-[20px]">{machine}</td>
      <td className={reviewed === 'Rebuilding' || reviewed === 'Not built' ? 'py-2 text-right text-[13px] text-beam-text-muted' : 'tabular py-2 text-right text-[16px] font-medium xl:text-[20px]'}>{reviewed}</td>
    </tr>
  );
}

export function MachineVersusHumanSummary({ matterRef, head, items, machine, reviewed, matterStatus, onOpenItem }: {
  matterRef: string;
  head: SortRunHead;
  items: SortReviewItem[];
  machine: CasefileManifest;
  reviewed: CasefileManifest | null;
  matterStatus: Status;
  onOpenItem: (itemId: string) => void;
}) {
  const changed = items.filter((i) => i.decision === 'corrected');
  const accepted = items.filter((i) => i.decision === 'accepted' && i.category !== 'additional');
  const decided = items.filter((i) => i.category !== 'additional' && i.decision !== 'unresolved').length;
  const pending = head.rebuild?.state === 'failed' ? 'Not built' : 'Rebuilding';
  const fig = (n: number | undefined): string => (reviewed && n !== undefined ? String(n) : pending);
  return (
    <section aria-label="Machine versus reviewed" data-slot="review-summary" className="flex flex-col gap-4 rounded-xl border border-beam-line bg-beam-paper p-4">
      <h2 className="eyebrow text-beam-text-muted">Machine versus reviewed</h2>
      <div className="overflow-x-auto">
        <table className="w-full max-w-[480px]">
          <thead>
            <tr><th className="sr-only">Figure</th><th scope="col" className="eyebrow pr-6 text-right text-beam-text-muted">Machine</th><th scope="col" className="eyebrow text-right text-beam-text-muted">Reviewed</th></tr>
          </thead>
          <tbody>
            <Figure label="Documents" machine={String(machine.documents.length)} reviewed={fig(reviewed?.documents.length)} />
            <Figure label="Billable pages" machine={String(machine.billablePages)} reviewed={fig(reviewed?.billablePages)} />
            <Figure label="Duplicate pages" machine={String(machine.duplicatePages)} reviewed={fig(reviewed?.duplicatePages)} />
            <Figure label="Review items" machine={String(head.machine.reviewItems)} reviewed={`${decided} decided`} />
          </tbody>
        </table>
      </div>
      <p className="text-[13px]">The machine result is kept as it is. Corrections are recorded as decisions; the reviewed manifest and Sorted.pdf are separate artefacts{head.reviewedPointer ? ` built by the rebuild at ${dateTime(head.reviewedPointer.builtAt).split(', ')[1]}` : ''}.</p>

      <div className="flex flex-col gap-2">
        <h3 className="eyebrow text-beam-text-muted">What changed <span className="tabular">{changed.length}</span></h3>
        {changed.length === 0 ? <p className="text-[13px] text-beam-text-muted">Every machine decision was accepted.</p> : (
          <div className="overflow-x-auto rounded-lg border border-beam-line">
            <table className="w-full text-[13px] leading-[1.3]">
              <thead className="bg-beam-cream-deep">
                <tr>
                  <th scope="col" className="eyebrow h-9 w-[110px] px-3 text-left text-beam-text-soft">Pages</th>
                  <th scope="col" className="eyebrow h-9 px-3 text-left text-beam-text-soft">Machine</th>
                  <th scope="col" className="eyebrow h-9 px-3 text-left text-beam-text-soft">Human</th>
                  <th scope="col" className="eyebrow h-9 w-[160px] px-3 text-left text-beam-text-soft">By</th>
                </tr>
              </thead>
              <tbody>
                {changed.map((i) => (
                  <tr key={i.itemId} className="border-t border-beam-line hover:bg-beam-cream-deep">
                    <td className="tabular px-3 py-3 align-top"><button type="button" className="text-beam-signature underline-offset-4 hover:underline" onClick={() => onOpenItem(i.itemId)}>{pagesText(i.startPage, i.endPage).replace(/^Pages? /, '')}</button>{i.category === 'additional' ? <span className="block text-xs text-beam-text-muted">additional</span> : null}</td>
                    <td className="px-3 py-3 align-top">{describeMachine(i, machine).sentence}</td>
                    <td className="px-3 py-3 align-top"><DecisionMark word="Corrected" size="table" /> <span>{describeHuman(i, machine).sentence.replace(/^Corrected(?: \(from page \d+\))?: /, '')}</span></td>
                    <td className="px-3 py-3 align-top text-beam-text-muted">{i.byName ?? i.by ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {accepted.length > 0 ? (
        <Collapsible>
          <CollapsibleTrigger className="group flex items-center gap-1 text-[13px] font-medium">
            <ChevronRightIcon aria-hidden="true" className="size-4 transition-transform group-data-[state=open]:rotate-90" /> Accepted <span className="tabular text-beam-text-muted">{accepted.length}</span>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <ul className="mt-2 flex flex-col divide-y divide-beam-line text-[13px]">
              {accepted.map((i) => (
                <li key={i.itemId} className="flex flex-wrap items-center gap-3 py-2">
                  <span className="tabular w-[100px] text-beam-text-muted">{pagesText(i.startPage, i.endPage)}</span>
                  <span>{describeHuman(i, machine).sentence}</span>
                  <span className="ml-auto text-beam-text-muted">{i.byName ?? i.by ?? ''}</span>
                </li>
              ))}
            </ul>
          </CollapsibleContent>
        </Collapsible>
      ) : null}

      <div className="flex flex-wrap items-center gap-4">
        {head.reviewedPointer ? <OpenPdfLink variant="outline" href={sortedPdfUrl(matterRef, head.runId, 'reviewed')}>Open reviewed Sorted.pdf</OpenPdfLink> : null}
        <OpenPdfLink href={sortedPdfUrl(matterRef, head.runId, 'machine')}>Open machine Sorted.pdf</OpenPdfLink>
      </div>

      <p className="flex flex-wrap items-center gap-2 text-[13px]">
        {matterStatus === 3 ? <>3 to 4 gate: {head.reviewedPointer ? <>open, to <StatusChip status={4} size="sm" /></> : 'waiting on the rebuild'}</> : matterStatus > 3 ? <>3 to 4 gate: the case has moved on ({matterStatus} {statusLabel(matterStatus)}).</> : <>3 to 4 gate: opens once the case reaches 3 Paperwork Received.</>}
      </p>
    </section>
  );
}
